/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import isNil from 'lodash/isNil';

import ErrorName from '../../../core/error/error-name';

/**
 * @import UaParser from 'ua-parser-js'
 * @import { FirmwareManager as IFirmwareManager } from '../../../core/firmware'
 */

/**
 * Správce firmware pro standardní webové technologie.
 * @implements {IFirmwareManager}
 */
export default class FirmwareManager {
  /**
   * Vytvoří instanci.
   * @param {UAParser} uaParser Knihovna UAParser.js.
   */
  constructor(uaParser) {
    this.uaParser = uaParser;

    const { major, name } = this.uaParser.getBrowser();
    this.runtimePlatform = Promise.resolve(
      `web-${normalizeValue(name)}-${normalizeValue(major)}`
    );
  }

  /**
   * Zjistí část názvu modelu s detekovaným prohlížečem.
   * @private
   * @returns {string} Část názvu modelu.
   */
  getBrowser() {
    const { major, name } = this.uaParser.getBrowser();
    if (isNil(name)) {
      return '';
    }
    return `${name} ${major}`;
  }

  /**
   * Zjistí část názvu modelu s detekovaným vykreslovacím jádrem.
   * @private
   * @returns {string} Část názvu modelu.
   */
  getEngine() {
    const { name, version } = this.uaParser.getEngine();
    if (isNil(name)) {
      return '';
    }
    return `${name} ${version}`;
  }

  /** @type {IFirmwareManager['getInfo']} */
  getInfo() {
    return new Promise((resolve) =>
      resolve({
        manufacturer: this.getManufacturer(),
        modelName: this.getModelName(),
        // Není možné zjistit verzi firmware.
        version: undefined
      })
    );
  }

  /**
   * @private
   * @returns {string | undefined} Výrobce zařízení, pokud je detekován.
   */
  getManufacturer() {
    const manufacturer = this.uaParser.getDevice().vendor;
    return isNil(manufacturer) ? undefined : manufacturer;
  }

  /**
   * Zjistí část názvu modelu s detekovaným názvem modelu.
   * @private
   * @returns {string} Část názvu modelu.
   */
  getModelExact() {
    const { model } = this.uaParser.getDevice();
    if (isNil(model)) {
      return '';
    }
    return model;
  }

  /**
   * @private
   * @returns {string | undefined} Název modelu zařízení, pokud je detekován.
   */
  getModelName() {
    const stringArray = [
      this.getModelExact(),
      this.getBrowser(),
      this.getEngine(),
      this.getOs()
    ];
    const modelName = stringArray.filter((string) => string).join(', ');
    return isNil(modelName) ? undefined : modelName;
  }

  /** @type {IFirmwareManager['getMultiInstanceId']} */
  getMultiInstanceId() {
    return new Promise((resolve) => resolve(null));
  }

  /**
   * Zjistí část názvu modelu s detekovaným operačním systémem.
   * @private
   * @returns {string} Část názvu modelu.
   */
  getOs() {
    const { name, version } = this.uaParser.getOS();
    if (isNil(name)) {
      return '';
    }
    return `${name} ${version}`;
  }

  /** @type {IFirmwareManager['getRuntimePlatform']} */
  async getRuntimePlatform() {
    return await this.runtimePlatform;
  }

  /** @type {IFirmwareManager['getSerialNumber']} */
  getSerialNumber() {
    return new Promise(() => {
      const error = new Error('Unable to get the serial number.');
      error.name = ErrorName.NotSupportedError;
      throw error;
    });
  }

  /** @type {IFirmwareManager['update']} */
  update(_) {
    return new Promise(() => {
      const error = new Error('Unable to do any update.');
      error.name = ErrorName.NotSupportedError;
      throw error;
    });
  }
}

/**
 * @param {string | undefined} str
 * @returns {string}
 */
function normalizeValue(str) {
  return (str ?? 'unknown').toLowerCase().replaceAll(' ', '_');
}
